<template>
    <div class="lx-online-booking-checkout-details">
        <b-row class="lx-online-booking-checkout-details__row">
            <div class="col-12 col-md">
                <div class="lx-online-booking-checkout-details__row__detail">
                    <div class="lx-online-booking-checkout-details__row__detail__title">
                        <font-awesome-icon :icon="['fas', 'user']" />
                        <span class="ml-2">Adults</span>
                    </div>
                    <div
                        class="lx-online-booking-checkout-details__row__detail__content"
                    >
                        <form-select
                            :key="adultsKey"
                            v-model="guests"
                            :options="guestsOptions"
                            v-if="isGuestsEditable"
                        />
                        <span :class="{'is-editable': isEditable}" v-else>{{ guests }}</span>
                    </div>
                </div>
            </div>
            <div class="col-12 col-md">
                <div class="lx-online-booking-checkout-details__row__detail">
                    <div class="lx-online-booking-checkout-details__row__detail__title">
                        <font-awesome-icon :icon="['fas', 'child-reaching']" />
                        <span class="ml-2">Children</span>
                    </div>
                    <div
                        class="lx-online-booking-checkout-details__row__detail__content"
                    >
                        <form-select
                            :key="childrenKey"
                            v-model="children"
                            :options="childrenOptions"
                            v-if="isChildrenEditable"
                        />
                        <span :class="{'is-editable': isEditable}" v-else>{{ children }}</span>
                    </div>
                </div>
            </div>
            <div class="col-12 col-md">
                <div class="lx-online-booking-checkout-details__row__detail">
                    <div class="lx-online-booking-checkout-details__row__detail__title">
                        <font-awesome-icon :icon="['fas', 'dog']" />
                        <span class="ml-2">Pets</span>
                        <fa-icon-tooltip
                            class="lx-online-booking-checkout-details__row__detail__title__tooltip"
                            :id="generateId()"
                            :icon="['fas', 'circle-info']"
                            variant="primary"
                            v-if="techReservation.property.is_pets && techReservation.property.pet_policy"
                        >
                            <p>{{ techReservation.property.pet_policy }}</p>
                        </fa-icon-tooltip>
                    </div>
                    <div
                        class="lx-online-booking-checkout-details__row__detail__content"
                    >
                        <form-select
                            :key="petsKey"
                            v-model="pets"
                            :options="petsOptions"
                            v-if="isPetsEditable"
                        />
                        <span :class="{'is-editable': isEditable}" v-else>{{ pets }}</span>
                    </div>
                </div>
            </div>
            <template v-if="techReservation.property.photos.length == 0">
                <div class="col-12 col-md">
                    <div class="lx-online-booking-checkout-details__row__detail">
                        <div class="lx-online-booking-checkout-details__row__detail__title">
                            <font-awesome-icon :icon="['fas', 'bed']" />
                            <span class="ml-2">Bedrooms</span>
                        </div>
                        <div
                            class="lx-online-booking-checkout-details__row__detail__content"
                        >
                            <span :class="{'is-editable': isEditable}">
                                {{ techReservation.property.bedrooms }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md">
                    <div class="lx-online-booking-checkout-details__row__detail">
                        <div class="lx-online-booking-checkout-details__row__detail__title">
                            <font-awesome-icon :icon="['fas', 'shower']" />
                            <span class="ml-2">Bathrooms</span>
                        </div>
                        <div
                            class="lx-online-booking-checkout-details__row__detail__content"
                        >
                            <span :class="{'is-editable': isEditable}">
                                {{ techReservation.property.baths }}
                            </span>
                        </div>
                    </div>
                </div>
            </template>
        </b-row>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import range from 'lodash/range';
    import FormSelect from '../../../generic/form-select/form-select.vue';
    import FaIconTooltip from '../../../generic/fa-icon-tooltip/fa-icon-tooltip.vue';

    export default {
        name: 'OnlineBookingCheckoutPropertyDetails',
        components: {
            FormSelect,
            FaIconTooltip,
        },
        props: {
            index: Number,
            isEditable: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                adultsKey: 1,
                childrenKey: 1,
                petsKey: 1,
            };
        },
        computed: {
            ...mapGetters('onlineBooking', ['reservation']),
            techReservation() {
                return this.reservation.tech_reservations[this.index];
            },            
            guests: {
                get() {
                    return this.techReservation.tech_reservation.guests;
                },
                async set(value) {
                    this.$store.commit('onlineBooking/gotBusy', true);
                    try {
                        await this.$store.dispatch(
                            'onlineBooking/updateTechReservation',
                            {
                                ...this.techReservation,
                                tech_reservation: {
                                    ...this.techReservation.tech_reservation,
                                    guests: value,
                                },
                            }
                        );
                    } catch {
                        this.errorToast({
                            text: 'Could not save adults.',
                        });
                    } finally {
                        this.$store.commit('onlineBooking/gotBusy', false);
                        this.adultsKey++;
                    }
                },
            },
            guestsOptions() {
                return range(
                    1,
                    this.techReservation.property.max_guests + 1
                ).map(n => ({
                    value: n,
                    text: n,
                }));
            },
            isGuestsEditable() {
                return this.isEditable;
            },
            children: {
                get() {
                    return this.techReservation.tech_reservation.children;
                },
                async set(value) {
                    this.$store.commit('onlineBooking/gotBusy', true);
                    try {
                        await this.$store.dispatch(
                            'onlineBooking/updateTechReservation',
                            {
                                ...this.techReservation,
                                tech_reservation: {
                                    ...this.techReservation.tech_reservation,
                                    children: value,
                                },
                            }
                        );
                    } catch {
                        this.errorToast({
                            text: 'Could not save children.',
                        });
                    } finally {
                        this.$store.commit('onlineBooking/gotBusy', false);
                        this.childrenKey++;
                    }
                },
            },
            childrenOptions() {
                return range(0, this.techReservation.property.max_guests).map(
                    n => ({
                        value: n,
                        text: n,
                        disabled: this.isChildrenValueDisabled(n, this.techReservation.property.is_children_rates),
                    })
                );
            },
            isChildrenEditable() {
                return (
                    this.isEditable &&
                    this.techReservation.property.is_children &&
                    this.techReservation.property.max_guests > 1
                );
            },
            pets: {
                get() {
                    return this.techReservation.property.is_pets
                        ? this.techReservation.tech_reservation.pets
                            ? this.techReservation.tech_reservation.number_of_pets
                            : 0
                        : 'Not allowed';
                },
                async set(value) {
                    this.$store.commit('onlineBooking/gotBusy', true);
                    const payload =
                        value === 0
                            ? { pets: false }
                            : { pets: true, number_of_pets: value };
                    try {
                        await this.$store.dispatch(
                            'onlineBooking/updateTechReservation',
                            {
                                ...this.techReservation,
                                tech_reservation: {
                                    ...this.techReservation.tech_reservation,
                                    ...payload,
                                },
                            }
                        );
                    } catch {
                        this.errorToast({
                            text: 'Could not save pets.',
                        });
                    } finally {
                        this.$store.commit('onlineBooking/gotBusy', false);
                        this.petsKey++;
                    }
                },
            },
            petsOptions() {
                return range(
                    0,
                    this.techReservation.property.maximum_pets + 1
                ).map(n => ({
                    value: n,
                    text: n,
                }));
            },
            isPetsEditable() {
                return (
                    this.isEditable &&
                    this.techReservation.property.is_pets
                );
            },
        },
        methods: {
            isChildrenValueDisabled(value, isChildrenRates) {
                return (
                    this.techReservation.tech_reservation.guests + value >
                        this.techReservation.property.max_guests && isChildrenRates
                );
            },
            generateId() {
                return (Math.random() * 1e128).toString(36);
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    .invalid {
        border: 1px solid red;
        border-radius: 5px;
    }

    .lx-online-booking-checkout-details {
        &__row {

            &__detail {
                &__title {
                    display: flex;
                    align-items: center;
                    letter-spacing: 0.5px;

                    &__tooltip {
                        margin-left: 2px;
                    }
                }

                &__content {
                    span {
                        display: flex;
                        height: calc(1.5em + 0.75rem + 2px);
                        align-items: center;
                        &.is-editable {
                            margin-top: 10px;
                        }
                    }
                    fieldset {
                        margin-top: 10px;
                    }
                }
            }
        }
    }
</style>
